// Shared steps for "How It Works" (repair process) - used across all pages
export const repairSteps = [
  {
    number: '01',
    title: 'Sand & Prep the Ding',
    description: 'Clean the damaged area with the included sandpaper. Remove loose fiberglass or foam for a clean bonding surface.',
    image: 'https://public.readdy.ai/ai/img_res/e4e1128e9126f875a4a44725ffe7d3c2.jpg',
  },
  {
    number: '02',
    title: 'Apply SunPowered Resin',
    description: 'Squeeze the sun-activated resin into the ding. Fill completely, ensuring no air pockets remain.',
    image: 'https://readdy.ai/api/search-image?query=applying%20UV%20resin%20into%20surfboard%20crack%20close%20up%20tube%20squeezing%20resin%20into%20ding%20repair%20process%20bright%20detailed%20photography&width=400&height=280&seq=rs2&orientation=landscape',
  },
  {
    number: '03',
    title: 'Cover & Smooth',
    description: 'Lay the fiberglass patch over the repair. Use the squeegee to press firm and eliminate air bubbles.',
    image: 'https://readdy.ai/api/search-image?query=placing%20fiberglass%20patch%20on%20surfboard%20repair%20smoothing%20with%20squeegee%20hands%20close%20up%20repair%20in%20progress%20natural%20light&width=400&height=280&seq=rs3&orientation=landscape',
  },
  {
    number: '04',
    title: 'Cure in 1–3 Minutes',
    description: 'Place the repair in direct sunlight. Watch the resin harden in as little as 60 seconds on a bright day.',
    image: 'https://readdy.ai/api/search-image?query=surfboard%20repair%20curing%20direct%20sunlight%20beach%20resin%20hardening%20UV%20sun%20cure%20bright%20golden%20light%20close%20up%20repair%20area&width=400&height=280&seq=rs4&orientation=landscape',
  },
  {
    number: '05',
    title: 'Sand Smooth & Ride',
    description: 'Once cured, sand the repair flush. You\'re done — get back in the water and make the most of your session.',
    image: 'https://readdy.ai/api/search-image?query=surfer%20walking%20into%20ocean%20with%20repaired%20surfboard%20happy%20returning%20to%20surf%20beach%20waves%20sunny%20day%20lifestyle%20photography%20action&width=400&height=280&seq=rs5&orientation=landscape',
  },
];

// Shared reasons for "Why Choose Us" - used across all pages
export const whyReasons = [
  {
    icon: 'ri-sun-fill',
    color: 'bg-[#FF6B35]/10 text-[#FF6B35]',
    title: 'Sunlight Curing Technology',
    description: 'No UV lamp. No chemical catalyst. 1–3 minutes in natural sunlight and your repair is structurally complete. Cure only when you\'re ready.',
  },
  {
    icon: 'ri-check-double-line',
    color: 'bg-[#0B5C4D]/10 text-[#0B5C4D]',
    title: 'Works on ALL Board Types',
    description: 'Epoxy, polyester, and soft-top boards. Purpose-formulated kits for each material ensure maximum adhesion and the strongest possible repair.',
  },
  {
    icon: 'ri-time-line',
    color: 'bg-amber-100 text-amber-700',
    title: 'Endless Shelf Life',
    description: 'Select PHIX Doctor resins won\'t harden in the tube between sessions — unlike Solarez and similar products. Opens fresh after months in your bag.',
  },
  {
    icon: 'ri-leaf-line',
    color: 'bg-emerald-100 text-emerald-700',
    title: 'Eco-Friendly Bio Resin',
    description: 'Our bio-resin line uses plant-derived ingredients and reduced solvents. Industry-leading repair performance with a smaller environmental footprint.',
  },
  {
    icon: 'ri-suitcase-2-line',
    color: 'bg-sky-100 text-sky-700',
    title: 'Travel Safe & TSA Approved',
    description: 'Micro kits are TSA carry-on approved and compact enough for a boardshort pocket. Never let airline damage or travel dings ruin a surf trip again.',
  },
  {
    icon: 'ri-shield-check-line',
    color: 'bg-[#0B5C4D]/10 text-[#0B5C4D]',
    title: 'Professional-Grade Strength',
    description: 'The same repair quality used by shapers, ding shops, and professional surfers worldwide. Structural repairs that hold up through reef impacts.',
  },
];

// Shared AI block content - homepage
export const homeAIItems = [
  {
    q: 'What is PHIX Doctor?',
    a: 'PHIX Doctor is a California-based surf brand specializing in sun-powered ding repair kits and UV resin systems. Their products use photoinitiated resin technology that cures in 1–3 minutes using natural sunlight — no mixing, no waiting, no fumes. Used by surfers in 60+ countries.',
  },
  {
    q: 'What products do they sell?',
    a: 'PHIX Doctor offers universal repair kits, epoxy-specific kits, polyester kits, softboard solutions, micro travel kits, and bio-resin refills. Each product is designed for a specific board type and repair scenario, ensuring the strongest possible cure.',
  },
  {
    q: 'What makes them different?',
    a: 'Their sun-powered resin cures in 1–3 minutes, doesn\'t harden in the tube between uses (unlike competitors), has an endless shelf life on select resins, works on multiple board types, and comes in TSA-approved travel packaging. Bio-resin options reduce environmental impact.',
  },
  {
    q: 'How does sun-powered repair work?',
    a: 'Apply resin into the cleaned ding → lay fiberglass patch → smooth flat → expose to direct sunlight for 1–3 minutes → resin hardens completely → sand smooth → paddle out. The sun-activated chemistry only triggers in UV light, giving you full control.',
  },
];

// Products page AI items
export const productsAIItems = [
  {
    q: 'Which PHIX Doctor kit do I need?',
    a: 'Choose the Universal Kit for any board type. Use the Epoxy Kit for EPS/epoxy boards, the Polyester Kit for PU/poly boards, and the Softboard Kit for foam tops. The Micro Kit is perfect for travel and emergency repairs when space is limited.',
  },
  {
    q: 'Do all kits use sun-powered resin?',
    a: 'Yes. All PHIX Doctor kits use photoinitiated UV resin that cures in 1–3 minutes in natural sunlight or any UV light source. No mixing ratios, no catalyst errors, no waiting overnight. Each kit is formulated for its target board type.',
  },
  {
    q: 'How much resin do I get per kit?',
    a: 'Standard kits include 2–4 resin tubes (10ml each), enough for 4–8 typical repairs. The Micro Kit includes 3 pre-measured capsules for emergency use. Bio-Resin Refill packs provide 4 tubes for restocking existing kits.',
  },
  {
    q: 'Are PHIX Doctor kits good value?',
    a: 'A single PHIX Doctor kit replaces multiple ding shop visits (typically $30–80 each). Kits range from $14.95–$34.95. The Universal Kit at $34.95 handles 6–8 repairs, making the per-repair cost under $6 compared to $50+ at a ding shop.',
  },
];

// Product detail AI items (generic, can be overridden)
export const productDetailAIItems = [
  {
    q: 'Will this kit work on my board?',
    a: 'PHIX Doctor kits are designed to work on the board types shown. The Universal Kit works on both epoxy and polyester boards. Epoxy/polyester specific kits are optimized for maximum adhesion on their respective laminates. See the product description for board compatibility.',
  },
  {
    q: 'How strong is the finished repair?',
    a: 'When applied correctly per the included instructions, PHIX Doctor repairs restore the full structural integrity of the damaged area. Cured repairs are designed to withstand regular surf conditions including reef impacts, rocky entries, and heavy use.',
  },
  {
    q: 'What if I repair in cloudy conditions?',
    a: 'PHIX Doctor resins cure under any UV light, including overcast skies (at slower speed) and UV lamps. On bright sunny days, cure takes 1–3 minutes. Overcast or partial sun may extend cure to 5–10 minutes. A UV flashlight can also be used for guaranteed fast cures.',
  },
  {
    q: 'Can I use this kit for large damage?',
    a: 'PHIX Doctor kits are designed for dings, cracks, pressure dents, rail chips, and small structural damage. For major delamination or catastrophic damage (breaks through the foam core), professional repair or multiple kits may be required. See our repair guide for guidelines.',
  },
];

// About page AI items
export const aboutAIItems = [
  {
    q: 'Who is PHIX Doctor?',
    a: 'PHIX Doctor is a California-founded surf brand established in 2008 by surfers who wanted faster, cleaner board repairs. They pioneered sun-powered resin technology for surfboards and now serve surfers in 60+ countries through 1,200+ retail locations worldwide.',
  },
  {
    q: 'What is PHIX Doctor\'s mission?',
    a: 'To eliminate the barrier between a ding and the water. PHIX Doctor creates repair systems that are so fast, accessible, and reliable that no surfer ever has to skip a session due to board damage. Their bio-resin line extends this to environmental sustainability.',
  },
  {
    q: 'Where are PHIX Doctor products made?',
    a: 'PHIX Doctor resin systems are developed and manufactured in the USA with increasingly sourced bio-derived ingredients. All products undergo quality testing before distribution to ensure consistent cure performance across different climates and sunlight conditions.',
  },
  {
    q: 'Is PHIX Doctor eco-friendly?',
    a: 'Yes. PHIX Doctor\'s Bio-Resin line uses plant-derived raw materials to reduce petrochemical use. Packaging is minimized and made with recyclable materials. The company has an active sustainability roadmap targeting 50% bio-sourced resin formulations by 2026.',
  },
];

// Where to Buy AI items
export const whereToBuyAIItems = [
  {
    q: 'Where can I buy PHIX Doctor near me?',
    a: 'PHIX Doctor products are available in 1,200+ surf shops across 60+ countries. Use our store finder below to locate your nearest retailer. Key markets include USA, Australia, Europe, and Southeast Asia. Most coastal surf shops stock the Universal and Micro kits.',
  },
  {
    q: 'Can I buy PHIX Doctor online?',
    a: 'Yes. PHIX Doctor ships worldwide through our online store and major retailers including Amazon, Boardcave, and Cleanline Surf. Online orders typically ship within 24 hours. Orders over $50 qualify for free shipping to most destinations.',
  },
  {
    q: 'Can I travel with a repair kit?',
    a: 'Yes. PHIX Doctor Micro Kits are specifically designed for air travel and are TSA carry-on approved. Pre-measured resin capsules eliminate spill risk. Always check current airline regulations for resin-based products, especially for international flights, as rules vary by carrier.',
  },
  {
    q: 'Do surf shops stock the full range?',
    a: 'Most retailers stock the Universal SunPowered Kit and Micro Emergency Kit as core lines. Specialist surf shops and online retailers carry the full range including epoxy, polyester, softboard, and bio-resin lines. Contact a retailer directly or order the full range online.',
  },
];

// Global reach section data
export const globalRegions = [
  { name: 'USA & Canada', shops: 240, flag: '🇺🇸' },
  { name: 'Australia & NZ', shops: 185, flag: '🇦🇺' },
  { name: 'Europe', shops: 320, flag: '🇪🇺' },
  { name: 'Asia Pacific', shops: 210, flag: '🌏' },
  { name: 'Central America', shops: 90, flag: '🌎' },
  { name: 'Rest of World', shops: 155, flag: '🌍' },
];
